import { FC, useState } from 'react';
import { Dialog, DialogContent } from '@material-ui/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileImage } from '@fortawesome/pro-regular-svg-icons';
import { Button, Flex, BoxV2 as Box, Grid } from 'portal-commons';
import CampaignAsset from '../../CampaignAsset';
import { BrandAsset, brandAssetController, BrandAssetType } from '@/apis/core';
import AssetVerificationModal from '../../AssetVerificationModal';

interface ModalProps {
  type: BrandAssetType;
  open: boolean;
  options?: Array<BrandAsset>;
  onClose?: () => void;
  onSave?: (asset: BrandAsset) => void;
  brandUid: string;
  defaultValue?: string; // the name of asset
}

const AssetSelectionModal: FC<ModalProps> = ({
  type,
  open,
  onClose,
  onSave,
  options = [],
  brandUid,
  defaultValue,
}) => {
  const [assetOptions, setAssetOptions] = useState<BrandAsset[]>(options);
  const [selectedAsset, setSelectedAsset] = useState<BrandAsset | null>(
    options.find((asset) => asset.attachmentUuid === defaultValue) || null
  );
  const [modalType, setModalType] = useState<'none' | 'verification'>('none');
  const [verifyingAsset, setVerifyingAsset] = useState<BrandAsset | null>(null);

  const handleSave = () => {
    if (onSave && selectedAsset) {
      onSave(selectedAsset);
    }
  };

  const handleSelectAsset = (uuid: string) => {
    const asset = options.find((asset) => asset.attachmentUuid === uuid);
    setSelectedAsset(asset || null);
  };

  const handleVerificationComplete = async (asset?: BrandAsset | null) => {
    if (!asset) return;
    handleSelectAsset(asset.attachmentUuid);
    const updatedAssets = await brandAssetController.getBrandAssets(brandUid, {
      brandAssetType: type,
    });
    setAssetOptions(updatedAssets);
    setModalType('none');
    setVerifyingAsset(null);
  };

  const openVerificationModal = (asset: BrandAsset) => {
    setVerifyingAsset(asset);
    setModalType('verification');
  };

  return (
    <Dialog
      open={open}
      maxWidth="md"
      BackdropProps={{
        style: {
          backdropFilter: 'blur(1px)',
        },
      }}
    >
      <DialogContent>
        <Flex
          as="h3"
          sx={{
            alignItems: 'center',
            gap: 'xs',
            color: 't.black80',
            flexDirection: 'row',
            fontSize: '24px',
            fontWeight: 'semiBold',
            my: 'xl',
            justifyContent: 'center',
          }}
        >
          <FontAwesomeIcon icon={faFileImage} fontSize="24" />
          {type === BrandAssetType.Banner ? 'Campaign Banner' : 'Campaign Logo'}
        </Flex>
        <Box
          as="p"
          sx={{ fontSize: 'H300', color: 't.black70', mt: 'xl', mb: 'l' }}
        >
          Select a {type.toLowerCase()} from your brand assets to use for your
          campaign.
        </Box>
        <Grid
          sx={{
            gridTemplateColumns: `repeat(${
              type === BrandAssetType.Banner ? 2 : 4
            }, minmax(${
              type === BrandAssetType.Banner ? '379px' : '152px'
            }, auto))`,
            gridGap: 0,
            columnGap: 'm',
            rowGap: 'm',
            maxHeight: '300px',
            overflowY: 'auto',
          }}
        >
          {assetOptions.map((asset) => (
            <CampaignAsset
              key={asset.name}
              name={asset.name}
              type={asset.type}
              uuid={asset.attachmentUuid}
              status={asset.verificationStatus}
              dimension={
                type === BrandAssetType.Banner
                  ? { width: '379px', height: '126px' }
                  : { width: '152px', height: '152px' }
              }
              onSelect={handleSelectAsset}
              selected={selectedAsset?.attachmentUuid === asset.attachmentUuid}
              onVerify={() => openVerificationModal(asset)}
            />
          ))}
        </Grid>
        <Flex
          sx={{
            columnGap: '18px',
            padding: '30px 0',
            flexDirection: 'row',
            justifyContent: 'center',
            gap: 'l',
          }}
        >
          <Button
            variant="outline"
            onClick={onClose}
            data-testid="reVerifyModalCancelButton"
          >
            Cancel
          </Button>
          <Button onClick={handleSave} data-testid="reVerifyModalSaveButton">
            Save
          </Button>
        </Flex>
      </DialogContent>
      <AssetVerificationModal
        open={modalType === 'verification'}
        brandUid={brandUid}
        type={verifyingAsset?.type}
        name={verifyingAsset?.name}
        uuid={verifyingAsset?.attachmentUuid}
        onCancel={() => {
          setModalType('none');
          setVerifyingAsset(null);
        }}
        onSubmit={() => handleVerificationComplete(verifyingAsset)}
      />
    </Dialog>
  );
};

export default AssetSelectionModal;
